import { addToCart, readCart, removeFromCart, setQuantity } from "./cart.js";
import { escapeHtml, money, productImage, showNotice, updateCartCount } from "./format.js";
import { deliverySummary, readFavorites, toggleFavorite } from "./storefront-model.js";
import { trackItems } from "./tracking.js";

export function syncFavorites() {
  const saved = readFavorites();
  document.querySelectorAll('[data-action="favorite"]').forEach((button) => {
    const active = saved.includes(button.dataset.productId);
    button.setAttribute("aria-pressed", String(active));
    button.setAttribute("aria-label", active ? "Прибрати з обраного" : "Додати до обраного");
  });
  document.querySelectorAll(".favorites-count").forEach((count) => {
    count.hidden = saved.length === 0;
    count.textContent = String(saved.length);
  });
}

export function attachShoppingUI(getProducts) {
  syncFavorites();
  if (document.documentElement.dataset.shoppingBound) return;
  document.documentElement.dataset.shoppingBound = "true";
  document.addEventListener("click", async (event) => {
    const step = event.target.closest('[data-action="decrease-product"], [data-action="increase-product"]');
    if (step) {
      const input = document.querySelector("#product-quantity");
      input.value = String(Math.max(1, Math.min(99, Math.trunc(Number(input.value) || 1) + (step.dataset.action === "increase-product" ? 1 : -1))));
      syncProductQuantity();
      return;
    }
    const favorite = event.target.closest('[data-action="favorite"]');
    if (favorite) {
      try { toggleFavorite(favorite.dataset.productId); syncFavorites(); }
      catch { showNotice("Не вдалося зберегти обране на цьому пристрої.", "error"); }
      return;
    }
    const button = event.target.closest('[data-action="add-product"]');
    if (!button || button.disabled) return;
    button.disabled = true;
    button.setAttribute("aria-busy", "true");
    try {
      const response = await fetch(`/api/products/${encodeURIComponent(button.dataset.productId)}`, { cache: "no-store" });
      if (!response.ok) throw new Error("PRODUCT_UNAVAILABLE");
      const { product } = await response.json();
      const quantity = button.closest(".product-detail") ? Math.max(1, Math.min(99, Math.trunc(Number(document.querySelector("#product-quantity")?.value) || 1))) : 1;
      addToCart(product, quantity);
      updateCartCount();
      trackItems("add_to_cart", [product], { [product.id]: quantity });
      await openCartPreview(getProducts);
    } catch {
      showNotice("Не вдалося оновити кошик. Перевірте з'єднання та відкрийте кошик перед повтором.", "error");
    } finally { button.disabled = false; button.removeAttribute("aria-busy"); }
  });
  document.addEventListener("input", (event) => {
    if (event.target.id === "product-quantity") syncProductQuantity();
  });
  window.addEventListener("storage", () => { syncFavorites(); updateCartCount(); });
}

function syncProductQuantity() {
  const input = document.querySelector("#product-quantity");
  const value = Math.trunc(Number(input?.value) || 1);
  const decrease = document.querySelector('[data-action="decrease-product"]');
  const increase = document.querySelector('[data-action="increase-product"]');
  if (decrease) decrease.disabled = value <= 1;
  if (increase) increase.disabled = value >= 99;
}

async function openCartPreview(getProducts) {
  document.querySelector("#cart-preview")?.remove();
  const dialog = document.createElement("dialog");
  dialog.id = "cart-preview";
  dialog.className = "cart-preview";
  dialog.setAttribute("aria-labelledby", "cart-preview-title");
  const catalog = (await getProducts()).products;
  const byId = new Map(catalog.map((p) => [p.id, p]));
  function render(focusAction, focusId) {
    const lines = readCart();
    const total = lines.reduce((sum, line) => sum + line.priceCents * line.quantity, 0);
    const stale = lines.some((line) => !byId.has(line.id) || byId.get(line.id).priceCents !== line.priceCents || byId.get(line.id).version !== line.version);
    dialog.innerHTML = `<div class="cart-preview-heading"><h2 id="cart-preview-title">Букет у кошику</h2><button class="icon-button" type="button" data-cart-modal="close" aria-label="Закрити кошик">×</button></div><div class="cart-preview-lines">${lines.map((line) => {
      const product = byId.get(line.id);
      return `<article class="cart-preview-line">${productImage(product || { name: line.id }, "thumbnail")}<div><a href="/products/${encodeURIComponent(line.id)}">${escapeHtml(product?.name || line.id)}</a><p>${money(line.priceCents * line.quantity)}</p><div class="modal-quantity"><button type="button" class="icon-button" data-cart-modal="decrease" data-id="${line.id}" aria-label="Зменшити кількість">-</button><span>${line.quantity}</span><button type="button" class="icon-button" data-cart-modal="increase" data-id="${line.id}" aria-label="Збільшити кількість" ${line.quantity >= 99 ? "disabled" : ""}>+</button></div><button class="text-link" type="button" data-cart-modal="remove" data-id="${line.id}">Видалити</button></div></article>`;
    }).join("") || '<p>Ваш кошик порожній</p>'}</div><p class="summary-row"><span>Сума за букети</span><strong>${money(total)}</strong></p><p class="helper-copy">${deliverySummary(total)}</p>${stale ? '<p class="inline-alert">Дані букета змінилися. Перевірте кошик перед оформленням.</p>' : ""}<a class="button button-primary button-full" href="${stale || !lines.length ? "/cart" : "/checkout"}">${stale ? "Перевірити кошик" : "Оформити замовлення"}</a><button class="button button-secondary button-full" type="button" data-cart-modal="close">Продовжити вибір</button>`;
    if (focusAction) dialog.querySelector(`[data-cart-modal="${focusAction}"][data-id="${focusId}"]`)?.focus();
    window.dispatchEvent(new CustomEvent("ko-public-content-updated"));
  }
  dialog.addEventListener("click", (event) => {
    const button = event.target.closest("[data-cart-modal]");
    if (!button) { if (event.target === dialog) { const r = dialog.getBoundingClientRect(); if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) dialog.close(); } return; }
    const action = button.dataset.cartModal;
    if (action === "close") return dialog.close();
    const line = readCart().find((item) => item.id === button.dataset.id);
    if (!line) return;
    if (action === "remove") removeFromCart(line.id);
    else setQuantity(line.id, line.quantity + (action === "increase" ? 1 : -1));
    render(action, line.id);
  });
  dialog.addEventListener("close", () => dialog.remove());
  document.body.append(dialog);
  render();
  dialog.showModal();
}
